/**
 * One voice call (web-plan.md F4): the signaling WS, the RTCPeerConnection
 * into the SFU's WebRTC bridge, the E2EE worker and the mixer, driven as one
 * object with one lifetime. The runtime creates it on join and drops it on
 * leave; everything the UI needs flows out through `VoiceEvents`.
 *
 * The protocol is the native client's, ported from bot/src/lib.rs:
 * Hello -> Auth -> JoinChannel(WebRtcJoin + provisional KeyPackage) ->
 * JoinAccept(sdp_answer, sender_id, roster, MLS group) -> the usable
 * KeyPackage or create_group -> the DS flow (`on_mls_message`, serialized).
 * The browser plane differences are D49's: the offer rides JoinChannel, the
 * SFU is ICE-lite and answers, DTLS-SRTP replaces the transport AEAD, and
 * the media-plane control messages ride a DataChannel instead of UDP.
 */
import {
  CAP_ANNOUNCE_ON_READY,
  CAP_VIEW_SCREEN_SHARE,
  PROTO_VER,
  decodeControl,
  decodeServer,
  encodeClient,
  encodeControl,
  type SMsg,
} from "./proto";
import { Mixer, SHARE_AUDIO_SLOT, VOICE_SLOTS } from "./mixer";
import type {
  FrameTransformOptions,
  VoiceWorkerEv,
  VoiceWorkerReq,
  VoiceWorkerRes,
} from "./worker";
import type { MlsMemberRef, Participant } from "../../gen/proto/echo/signal/v1/signal_pb";

export interface VoicePeerInfo {
  userId: number;
  senderId: number;
  username: string;
  muted: boolean;
  deafened: boolean;
  serverMuted: boolean;
  rttMs: number;
  screenSharing: boolean;
  screenSharePaused: boolean;
  screenShareHasAudio: boolean;
}

export type VoiceEndReason = "callEnded" | "channelDeleted" | "dropped" | "serverError";

export interface VoiceEvents {
  /** Media connected AND the MLS group usable — the call is really up. */
  onUp(): void;
  onRoster(peers: VoicePeerInfo[]): void;
  onPeerJoined(peer: VoicePeerInfo): void;
  onPeerLeft(userId: number): void;
  onPeerVoice(userId: number, muted: boolean, deafened: boolean): void;
  onPeerRtt(userId: number, rttMs: number): void;
  onServerMute(userId: number, muted: boolean): void;
  /** Who the SFU currently forwards (top-3), as user ids, self included. */
  onSpeakers(userIds: number[]): void;
  /** Who each stream is being forwarded to, streamer → viewers, as user ids
   * and self included (screenshare-plan.md D336). The relay's own table, the
   * whole room at once; fired only when it changes. */
  onAudience(audience: Record<number, number[]>): void;
  /** The R16 verification code; changes on every epoch. */
  onCode(code: string): void;
  onStats(stats: { rttMs: number; lossPct: number; concealmentPct: number }): void;
  /** A peer's screen share started/stopped/changed (F5). `reason` is the
   * ScreenShareEnd code, meaningful only when active is false. */
  onPeerShare(
    userId: number,
    share: { active: boolean; paused: boolean; hasAudio: boolean; reason: number },
  ): void;
  /** The call died under us (never fired for our own `leave()`). */
  onEnded(reason: VoiceEndReason, detail?: string): void;
}

const STATS_PERIOD_MS = 10_000;
const EXPECT_TIMEOUT_MS = 15_000;

/** R46's gate: no encoded-frame transform, no E2EE, no call. */
export function voiceSupported(): boolean {
  return "RTCRtpScriptTransform" in globalThis;
}

/** F5's gate: no WebCodecs H264, no stream viewing — and caps bit 0 stays
 * off so a sharer's picker warns about us instead of promising. */
export function shareViewSupported(): boolean {
  return "VideoDecoder" in globalThis;
}

/** The one SDP munge the plan pins: the voice ladder's Opus settings. */
function mungeOpus(sdp: string): string {
  const m = sdp.match(/a=rtpmap:(\d+) opus\/48000\/2/);
  if (!m) return sdp;
  const pt = m[1];
  const params = "minptime=20;useinbandfec=1;usedtx=1;stereo=0;sprop-stereo=0";
  const fmtp = new RegExp(`a=fmtp:${pt} [^\r\n]*`);
  if (fmtp.test(sdp)) {
    return sdp.replace(new RegExp(`a=fmtp:${pt} [^\r\n]*`, "g"), `a=fmtp:${pt} ${params}`);
  }
  return sdp.replace(
    new RegExp(`(a=rtpmap:${pt} opus/48000/2\r?\n)`, "g"),
    `$1a=fmtp:${pt} ${params}\r\n`,
  );
}

function peerOf(p: Participant): VoicePeerInfo {
  return {
    userId: Number(p.userId),
    senderId: p.senderId,
    username: p.username,
    muted: p.muted,
    deafened: p.deafened,
    serverMuted: p.serverMuted,
    rttMs: p.rttMs,
    screenSharing: p.screenSharing,
    screenSharePaused: p.screenSharePaused,
    screenShareHasAudio: p.screenShareHasAudio,
  };
}

interface Waiter {
  cases: string[];
  resolve: (m: SMsg) => void;
  reject: (e: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export class VoiceSession {
  private jwt: string;
  private events: VoiceEvents;

  private ws: WebSocket | null = null;
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private worker: Worker | null = null;
  private mixer: Mixer | null = null;
  private mic: MediaStream | null = null;

  private channelId = 0;
  private userId = 0;
  private selfSenderId = 0;
  /** Group incarnation guard: every MlsCommitBundle must echo it. */
  private mlsGroupId: Uint8Array = new Uint8Array();

  private muted = false;
  private deafened = false;
  private mediaUp = false;
  private mlsActive = false;
  private readySent = false;
  private closed = false;

  private waiters: Waiter[] = [];
  private rpcSeq = 1;
  private rpcPending = new Map<number, { resolve: (r: VoiceWorkerRes) => void; reject: (e: Error) => void }>();
  private rejectOpen: ((e: Error) => void) | null = null;
  /** MLS ops apply strictly in arrival order (the DS is a total order). */
  private mlsChain: Promise<void> = Promise.resolve();

  private senderToUser = new Map<number, number>();
  private slotSender: Record<number, number> = {};
  private volumes = new Map<number, number>();
  /** The last audience handed to `onAudience`, serialized: the relay repeats
   * its snapshot every second, and a repeat is not news (D336). */
  private audienceSent = "";

  private statsTimer: ReturnType<typeof setInterval> | null = null;
  private prevStats = new Map<string, Record<string, number>>();

  // ---- share viewing (F5) ----
  private shareDc: RTCDataChannel | null = null;
  private watchingSender: number | null = null;
  private painter: ((senderId: number, frame: VideoFrame) => void) | null = null;
  private shareBytes = 0;
  private sharePrevBytes = 0;
  private framesPainted = 0;
  private prevFramesPainted = 0;
  private keyframesRequested = 0;

  constructor(opts: { jwt: string; events: VoiceEvents }) {
    this.jwt = opts.jwt;
    this.events = opts.events;
  }

  // -------------------------------------------------------------------------
  // Join

  async join(channelId: number, startMuted: boolean): Promise<void> {
    this.checkOpen();
    this.channelId = channelId;
    this.muted = startMuted;

    // The microphone first: its refusal is the most common failure and the
    // cheapest moment to fail — nothing else is allocated yet.
    const mic = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
    });
    if (this.closed) {
      mic.getTracks().forEach((track) => track.stop());
      this.checkOpen();
    }
    this.mic = mic;
    const micTrack = mic.getAudioTracks()[0];
    if (!micTrack) throw new Error("no microphone track");
    micTrack.enabled = !startMuted;

    this.mixer = new Mixer();
    await this.mixer.resume();
    this.checkOpen(); // still inside the join click's gesture

    this.worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (e: MessageEvent<VoiceWorkerRes | VoiceWorkerEv>) =>
      this.onWorkerMessage(e.data);

    await this.openWs();
    this.checkOpen();
    this.sendC({
      case: "hello",
      value: {
        protoVer: PROTO_VER,
        clientVer: "echofloat-web/0.1.0",
        caps: CAP_ANNOUNCE_ON_READY | (shareViewSupported() ? CAP_VIEW_SCREEN_SHARE : 0n),
      },
    });
    await this.expect(["helloAck"]);
    this.checkOpen();
    this.sendC({ case: "auth", value: { jwt: this.jwt } });
    const authAck = await this.expect(["authAck"]);
    this.checkOpen();
    if (authAck.case !== "authAck") throw new Error("unreachable");
    this.userId = Number(authAck.value.userId);

    // The provisional KeyPackage (never consumed): the leaf identity needs
    // the GW-assigned sender_id, which only JoinAccept carries.
    const provisional = await this.rpc({ id: 0, op: "provisionalKp", userId: this.userId });
    this.checkOpen();
    if (provisional.r !== "bytes") throw new Error("key package failed");

    const sdpOffer = mungeOpus(await this.buildPeerConnection(micTrack));
    this.checkOpen();

    this.sendC({
      case: "joinChannel",
      value: {
        channelId: BigInt(channelId),
        mlsKeyPackage: provisional.bytes,
        transport: { case: "webrtc", value: { sdpOffer } },
      },
    });
    const accepted = await this.expect(["joinAccept"]);
    this.checkOpen();
    if (accepted.case !== "joinAccept") throw new Error("unreachable");
    const ja = accepted.value;
    if (ja.transport.case !== "webrtc") {
      throw new Error("gateway answered with the UDP plane");
    }
    this.selfSenderId = ja.senderId;
    this.mlsGroupId = ja.mlsGroupId;

    const peers = ja.participants.map(peerOf);
    for (const p of peers) this.senderToUser.set(p.senderId, p.userId);
    this.events.onRoster(peers);

    // The real session, under the assigned identity.
    await this.rpc({ id: 0, op: "init", userId: this.userId, senderId: ja.senderId });
    this.checkOpen();
    if (ja.mlsCreate) {
      const created = await this.rpc({ id: 0, op: "create", groupId: this.mlsGroupId });
      this.checkOpen();
      if (created.r === "ok" && created.code) this.noteCode(created.code, true);
    } else {
      const kp = await this.rpc({ id: 0, op: "keyPackage" });
      this.checkOpen();
      if (kp.r !== "bytes") throw new Error("key package failed");
      this.sendC({
        case: "mlsKeyPackage",
        value: { channelId: BigInt(channelId), keyPackage: kp.bytes },
      });
    }

    await this.pc!.setRemoteDescription({
      type: "answer",
      sdp: ja.transport.value.sdpAnswer,
    });
    this.checkOpen();

    // The room learns the state we joined with.
    this.sendC({ case: "voiceState", value: { muted: this.muted, deafened: false } });
    this.startStats();
  }

  /** All six m-lines in the fixed order the bridge maps: mic sendonly, three
   * voice slots, share video, share audio — then the control DataChannel.
   * Resolves to the (unmunged) offer SDP. */
  private async buildPeerConnection(micTrack: MediaStreamTrack): Promise<string> {
    const pc = new RTCPeerConnection({ bundlePolicy: "max-bundle" });
    this.pc = pc;
    pc.onconnectionstatechange = () => {
      if (this.closed) return;
      if (pc.connectionState === "connected") {
        this.mediaUp = true;
        this.maybeReady();
      } else if (pc.connectionState === "failed") {
        this.end("dropped");
      }
    };

    const Transform = (
      globalThis as unknown as {
        RTCRtpScriptTransform: new (w: Worker, o: unknown) => unknown;
      }
    ).RTCRtpScriptTransform;
    const attach = (target: RTCRtpSender | RTCRtpReceiver, options: FrameTransformOptions) => {
      (target as unknown as { transform: unknown }).transform = new Transform(
        this.worker!,
        options,
      );
    };

    const micTx = pc.addTransceiver(micTrack, { direction: "sendonly" });
    attach(micTx.sender, { dir: "send", kind: 0 });

    for (let slot = 0; slot < VOICE_SLOTS; slot++) {
      const tx = pc.addTransceiver("audio", { direction: "recvonly" });
      attach(tx.receiver, { dir: "recv", kind: 0, slot });
      this.mixer!.attach(slot, tx.receiver.track);
    }
    const shareVideo = pc.addTransceiver("video", { direction: "recvonly" });
    attach(shareVideo.receiver, { dir: "recv", kind: 1 });
    const shareAudio = pc.addTransceiver("audio", { direction: "recvonly" });
    attach(shareAudio.receiver, { dir: "recv", kind: 2, slot: SHARE_AUDIO_SLOT });
    this.mixer!.attach(SHARE_AUDIO_SLOT, shareAudio.receiver.track);

    // No ClientHello here, ever: DTLS already authenticated this path, so
    // the UDP plane's handshake would be a lie the bridge refuses to hear.
    // The channel speaks plain ControlMessage: SpeakerSet/SenderFeedback
    // down; Subscribe/Unsubscribe/RequestKeyframe/ReceiverReport/Goodbye up.
    // The bridge routes by LABEL: "control" and "share" (F5, D210).
    const dc = pc.createDataChannel("control", { ordered: true });
    this.dc = dc;
    dc.binaryType = "arraybuffer";
    dc.onmessage = (e: MessageEvent<ArrayBuffer>) => this.onControl(e.data);

    // Share video's lane (D210): framed SFrame AUs, decoded by WebCodecs in
    // the worker. Created up front so watching never renegotiates (D50).
    const share = pc.createDataChannel("share", { ordered: true });
    this.shareDc = share;
    share.binaryType = "arraybuffer";
    share.onmessage = (e: MessageEvent<ArrayBuffer>) => {
      if (this.closed) return;
      this.shareBytes += e.data.byteLength;
      this.worker?.postMessage({ sh: e.data }, [e.data]);
    };

    const offer = await pc.createOffer();
    this.checkOpen();
    await pc.setLocalDescription(offer);
    this.checkOpen();
    // ICE-lite remote: it never checks, only answers ours — no trickle to
    // wait for, the offer is complete as created.
    return offer.sdp ?? "";
  }

  // -------------------------------------------------------------------------
  // Live controls

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyMicGate();
    this.sendC({ case: "voiceState", value: { muted: this.muted, deafened: this.deafened } });
  }

  setDeafened(deafened: boolean): void {
    this.deafened = deafened;
    this.mixer?.setDeafened(deafened);
    this.applyMicGate();
    this.sendC({ case: "voiceState", value: { muted: this.muted, deafened: this.deafened } });
  }

  /** Deafened implies the microphone is shut too — one decision (callbar.rs). */
  private applyMicGate(): void {
    const track = this.mic?.getAudioTracks()[0];
    if (track) track.enabled = !(this.muted || this.deafened);
  }

  setVolume(userId: number, volume: number): void {
    this.volumes.set(userId, volume);
    this.applyVolumes();
  }

  private applyVolumes(): void {
    for (const [slotStr, senderId] of Object.entries(this.slotSender)) {
      const slot = Number(slotStr);
      if (slot >= VOICE_SLOTS) continue;
      const userId = this.senderToUser.get(senderId);
      const volume = userId !== undefined ? (this.volumes.get(userId) ?? 1) : 1;
      this.mixer?.setSlotGain(slot, volume);
    }
  }

  // -------------------------------------------------------------------------
  // Share viewing (F5)

  /** The component that owns the canvas registers its painter here; frames
   * arrive decoded (VideoFrame) and MUST be closed by the painter. */
  setPainter(painter: ((senderId: number, frame: VideoFrame) => void) | null): void {
    this.painter = painter;
  }

  /** Subscribe to a peer's stream: video always, share audio if it has any.
   * Forwarding is a control message straight to the SFU (D87) — no gateway
   * round trip between the click and the first frame. */
  watch(peer: { senderId: number; hasAudio: boolean }): void {
    if (this.watchingSender === peer.senderId) return;
    if (this.watchingSender !== null) this.unwatch();
    this.watchingSender = peer.senderId;
    this.sendControl({
      case: "subscribeStream",
      value: { senderId: peer.senderId, payloadType: 1 },
    });
    if (peer.hasAudio) {
      this.sendControl({
        case: "subscribeStream",
        value: { senderId: peer.senderId, payloadType: 2 },
      });
    }
  }

  /** Unsubscribe both lanes (D106: an open subscription nobody watches costs
   * 2 Mbit/s on both ends) and drop the worker's decoder state. */
  unwatch(): void {
    const sender = this.watchingSender;
    if (sender === null) return;
    this.watchingSender = null;
    this.sendControl({ case: "unsubscribeStream", value: { senderId: sender, payloadType: 1 } });
    this.sendControl({ case: "unsubscribeStream", value: { senderId: sender, payloadType: 2 } });
    this.worker?.postMessage({ shReset: true });
  }

  /** The share-audio lane's own volume (D99), same 0..2 scale as voices. */
  setShareVolume(volume: number): void {
    this.mixer?.setSlotGain(SHARE_AUDIO_SLOT, volume);
  }

  leave(): void {
    if (this.closed) return;
    try {
      this.sendC({ case: "leaveChannel", value: { channelId: BigInt(this.channelId) } });
      this.sendControl({ case: "goodbye", value: {} });
    } catch {
      /* leaving a dead socket is still leaving */
    }
    this.teardown();
  }

  // -------------------------------------------------------------------------
  // Signaling plumbing

  private checkOpen(): void {
    if (this.closed) throw new DOMException("the call is over", "AbortError");
  }


  private openWs(): Promise<void> {
    this.checkOpen();
    const proto = location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${location.host}/v1/ws`);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    ws.onmessage = (e: MessageEvent<ArrayBuffer>) => this.dispatch(decodeServer(e.data));
    ws.onclose = () => {
      if (!this.closed) this.end("dropped");
    };
    return new Promise((resolve, reject) => {
      this.rejectOpen = reject;
      ws.onopen = () => {
        this.rejectOpen = null;
        if (this.closed) {
          ws.close();
          reject(new Error("the call is over"));
        } else {
          resolve();
        }
      };
      ws.onerror = () => {
        this.rejectOpen = null;
        reject(new Error("voice socket failed"));
      };
    });
  }

  private sendC(msg: Parameters<typeof encodeClient>[0]): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(encodeClient(msg));
  }

  private sendControl(msg: Parameters<typeof encodeControl>[0]): void {
    if (this.dc?.readyState === "open") this.dc.send(encodeControl(msg));
  }

  private expect(cases: string[]): Promise<SMsg> {
    this.checkOpen();
    return new Promise((resolve, reject) => {
      const waiter: Waiter = {
        cases,
        resolve,
        reject,
        timer: setTimeout(() => {
          this.waiters = this.waiters.filter((w) => w !== waiter);
          reject(new Error("the gateway did not answer"));
        }, EXPECT_TIMEOUT_MS),
      };
      this.waiters.push(waiter);
    });
  }

  private dispatch(msg: SMsg): void {
    if (this.closed || !msg.case) return;

    // A waiter first: the join flow's awaited replies.
    const idx = this.waiters.findIndex((w) => w.cases.includes(msg.case as string));
    if (idx >= 0) {
      const w = this.waiters.splice(idx, 1)[0];
      if (w) {
        clearTimeout(w.timer);
        w.resolve(msg);
      }
      return;
    }
    // A refusal or an error rejects every outstanding wait; outside a wait,
    // an Error means the server ended us.
    if (msg.case === "joinReject" || msg.case === "error") {
      const text = msg.case === "joinReject" ? msg.value.reason : msg.value.message;
      const pending = this.waiters.splice(0);
      for (const w of pending) {
        clearTimeout(w.timer);
        w.reject(new Error(text || msg.case));
      }
      if (pending.length === 0 && msg.case === "error") this.end("serverError", text);
      return;
    }

    switch (msg.case) {
      case "userJoined": {
        const p = msg.value.participant;
        if (!p) return;
        const peer = peerOf(p);
        this.senderToUser.set(peer.senderId, peer.userId);
        this.applyVolumes();
        this.events.onPeerJoined(peer);
        return;
      }
      case "userLeft": {
        this.senderToUser.delete(msg.value.senderId);
        this.events.onPeerLeft(Number(msg.value.userId));
        return;
      }
      case "peerVoiceState":
        this.events.onPeerVoice(Number(msg.value.userId), msg.value.muted, msg.value.deafened);
        return;
      case "peerStats":
        this.events.onPeerRtt(Number(msg.value.userId), msg.value.rttMs);
        return;
      case "serverMuteChanged":
        this.events.onServerMute(Number(msg.value.userId), msg.value.muted);
        return;
      case "peerScreenShare":
        this.events.onPeerShare(Number(msg.value.userId), {
          active: msg.value.active,
          paused: msg.value.paused,
          hasAudio: msg.value.hasAudio,
          reason: msg.value.reason,
        });
        return;
      case "callEnded":
        this.end("callEnded");
        return;
      case "channelDeleted":
        this.end("channelDeleted");
        return;
      case "mlsWelcome":
      case "mlsAddRequest":
      case "mlsRemoveRequest":
      case "mlsCommit":
      case "mlsCommitReject":
      case "mlsReset": {
        // Serialize: the DS is a total order, and two interleaved awaits
        // here would let a commit outrun the welcome it builds on.
        const m = msg;
        this.mlsChain = this.mlsChain.then(() => this.onMls(m)).catch(() => {});
        return;
      }
      default:
        return;
    }
  }

  /** The bot's `on_mls_message`, with the session behind an RPC. */
  private async onMls(msg: SMsg): Promise<void> {
    if (this.closed) return;
    switch (msg.case) {
      case "mlsWelcome": {
        const res = await this.rpc({ id: 0, op: "welcome", welcome: msg.value.welcome });
        this.checkOpen();
        if (res.r === "ok") {
          this.mlsGroupId = msg.value.groupId;
          if (res.code) this.noteCode(res.code, true);
        }
        return;
      }
      case "mlsAddRequest": {
        const req = msg.value;
        const adds = [];
        const added: MlsMemberRef[] = [];
        for (const c of req.add) {
          if (!c.member) continue;
          adds.push({
            userId: Number(c.member.userId),
            senderId: c.member.senderId,
            keyPackage: c.keyPackage,
          });
          added.push(c.member);
        }
        const res = await this.rpc({ id: 0, op: "add", epoch: Number(req.epoch), adds });
        this.checkOpen();
        if (res.r === "bundle") {
          this.sendC({
            case: "mlsCommitBundle",
            value: {
              channelId: req.channelId,
              epoch: req.epoch,
              commit: res.commit,
              welcome: res.welcome,
              added,
              removed: [],
              groupId: this.mlsGroupId,
            },
          });
        }
        return;
      }
      case "mlsRemoveRequest": {
        const req = msg.value;
        const removes = req.remove.map((m) => ({
          userId: Number(m.userId),
          senderId: m.senderId,
        }));
        const res = await this.rpc({ id: 0, op: "remove", epoch: Number(req.epoch), removes });
        this.checkOpen();
        if (res.r === "bundle") {
          this.sendC({
            case: "mlsCommitBundle",
            value: {
              channelId: req.channelId,
              epoch: req.epoch,
              commit: res.commit,
              welcome: new Uint8Array(),
              added: [],
              removed: req.remove,
              groupId: this.mlsGroupId,
            },
          });
        }
        return;
      }
      case "mlsCommit": {
        const res = await this.rpc({
          id: 0,
          op: "commit",
          commit: msg.value.commit,
          removedNonempty: msg.value.removed.length > 0,
        });
        this.checkOpen();
        if (res.r === "ok" && res.code) this.noteCode(res.code, res.active ?? false);
        return;
      }
      case "mlsCommitReject": {
        await this.rpc({ id: 0, op: "reject" });
        this.checkOpen();
        return;
      }
      case "mlsReset": {
        const r = msg.value;
        this.mlsGroupId = r.groupId;
        if (r.creator) {
          const res = await this.rpc({ id: 0, op: "create", groupId: r.groupId });
          this.checkOpen();
          if (res.r === "ok" && res.code) this.noteCode(res.code, true);
        } else {
          const kp = await this.rpc({ id: 0, op: "keyPackage" });
          this.checkOpen();
          if (kp.r === "bytes") {
            this.sendC({
              case: "mlsKeyPackage",
              value: { channelId: BigInt(this.channelId), keyPackage: kp.bytes },
            });
          }
        }
        return;
      }
      default:
        return;
    }
  }

  private noteCode(code: string, active: boolean): void {
    this.events.onCode(code);
    if (active && !this.mlsActive) {
      this.mlsActive = true;
      this.maybeReady();
    }
  }

  /** caps bit 1's promise: announce only when someone joining would actually
   * be heard — media connected and the group usable. Repeats are harmless. */
  private maybeReady(): void {
    if (!this.mediaUp || !this.mlsActive || this.readySent || this.closed) return;
    this.readySent = true;
    this.sendC({ case: "channelReady", value: { channelId: BigInt(this.channelId) } });
    this.events.onUp();
  }

  // -------------------------------------------------------------------------
  // Worker plumbing

  private rpc(req: VoiceWorkerReq): Promise<VoiceWorkerRes> {
    const id = this.rpcSeq++;
    return new Promise((resolve, reject) => {
      if (!this.worker) return reject(new Error("no worker"));
      this.rpcPending.set(id, { resolve, reject });
      this.worker.postMessage({ ...req, id });
    });
  }

  private onWorkerMessage(msg: VoiceWorkerRes | VoiceWorkerEv): void {
    if (this.closed) {
      if ("ev" in msg && msg.ev === "frame") msg.frame.close();
      return;
    }
    if ("ev" in msg) {
      if (msg.ev === "slot") {
        this.slotSender[msg.slot] = msg.senderId;
        this.applyVolumes();
      } else if (msg.ev === "code") {
        this.noteCode(msg.code, msg.active);
      } else if (msg.ev === "frame") {
        if (this.painter && this.watchingSender !== null) {
          this.framesPainted++;
          this.painter(msg.senderId, msg.frame);
        } else {
          msg.frame.close();
        }
      } else if (msg.ev === "keyframeNeeded") {
        this.keyframesRequested++;
        this.sendControl({ case: "requestKeyframe", value: { senderId: msg.senderId } });
      }
      return;
    }
    const done = this.rpcPending.get(msg.id);
    if (done) {
      this.rpcPending.delete(msg.id);
      done.resolve(msg);
    }
  }

  // -------------------------------------------------------------------------
  // Control channel + stats

  private onControl(bytes: ArrayBuffer): void {
    if (this.closed) return;
    const msg = decodeControl(bytes);
    if (msg.case === "speakerSet") {
      const ids: number[] = [];
      for (const senderId of msg.value.senderIds) {
        const userId = this.personOf(senderId);
        if (userId !== undefined) ids.push(userId);
      }
      this.events.onSpeakers(ids);
    } else if (msg.case === "streamAudience") {
      // D336: who each stream is forwarded to, in people. A sender the roster
      // does not know yet — a viewer whose join the gateway is still holding
      // back — is left out rather than guessed at; the relay repeats the
      // snapshot every second, and the next one after the roster catches up
      // has them.
      const audience: Record<number, number[]> = {};
      for (const entry of msg.value.streams) {
        const streamer = this.personOf(entry.senderId);
        if (streamer === undefined) continue;
        audience[streamer] = entry.viewers
          .map((senderId) => this.personOf(senderId))
          .filter((userId): userId is number => userId !== undefined)
          .sort((a, b) => a - b);
      }
      const sent = JSON.stringify(audience);
      if (sent !== this.audienceSent) {
        this.audienceSent = sent;
        this.events.onAudience(audience);
      }
    }
    // SenderFeedback and the rest drive nothing yet: the browser's encoder
    // ladder is Chrome's own (bandwidth estimation over transport-cc).
  }

  /** A sender id on this call as the person behind it — ourselves included,
   * which the roster alone does not hold. */
  private personOf(senderId: number): number | undefined {
    return senderId === this.selfSenderId ? this.userId : this.senderToUser.get(senderId);
  }

  private startStats(): void {
    this.statsTimer = setInterval(() => void this.reportStats().catch(() => {}), STATS_PERIOD_MS);
  }

  private async reportStats(): Promise<void> {
    const pc = this.pc;
    if (!pc || this.closed) return;
    let rttMs = 0;
    let jitterMs = 0;
    let lost = 0;
    let received = 0;
    let concealed = 0;
    let samples = 0;
    let sentBytes = 0;
    const report = await pc.getStats();
    this.checkOpen();
    report.forEach((s: Record<string, unknown>) => {
      const type = s.type as string;
      const id = s.id as string;
      if (type === "candidate-pair" && s.nominated && s.state === "succeeded") {
        rttMs = Math.round(((s.currentRoundTripTime as number) ?? 0) * 1000);
      } else if (type === "inbound-rtp" && s.kind === "audio") {
        const prev = this.prevStats.get(id) ?? {};
        lost += Math.max(0, ((s.packetsLost as number) ?? 0) - (prev.packetsLost ?? 0));
        received += Math.max(
          0,
          ((s.packetsReceived as number) ?? 0) - (prev.packetsReceived ?? 0),
        );
        concealed += Math.max(
          0,
          ((s.concealedSamples as number) ?? 0) - (prev.concealedSamples ?? 0),
        );
        samples += Math.max(
          0,
          ((s.totalSamplesReceived as number) ?? 0) - (prev.totalSamplesReceived ?? 0),
        );
        jitterMs = Math.max(jitterMs, Math.round(((s.jitter as number) ?? 0) * 1000));
        this.prevStats.set(id, {
          packetsLost: (s.packetsLost as number) ?? 0,
          packetsReceived: (s.packetsReceived as number) ?? 0,
          concealedSamples: (s.concealedSamples as number) ?? 0,
          totalSamplesReceived: (s.totalSamplesReceived as number) ?? 0,
        });
      } else if (type === "outbound-rtp" && s.kind === "audio") {
        const prev = this.prevStats.get(id) ?? {};
        sentBytes = Math.max(0, ((s.bytesSent as number) ?? 0) - (prev.bytesSent ?? 0));
        this.prevStats.set(id, { bytesSent: (s.bytesSent as number) ?? 0 });
      }
    });
    const lossPct = received + lost > 0 ? (lost / (received + lost)) * 100 : 0;
    const concealmentPct = samples > 0 ? (concealed / samples) * 100 : 0;
    this.sendC({
      case: "voiceStats",
      value: {
        lossPct,
        rttMs,
        jitterMs,
        concealmentPct,
        bitrateBps: Math.round((sentBytes * 8) / (STATS_PERIOD_MS / 1000)),
      },
    });
    this.events.onStats({ rttMs, lossPct, concealmentPct });

    // D90: stream telemetry only from the people in the stream. A viewer
    // reports what it RECEIVED (sending = false); fps here is frames that
    // made it to paint, which is the number a person would argue about.
    if (this.watchingSender !== null) {
      const bytes = this.shareBytes - this.sharePrevBytes;
      const frames = this.framesPainted - this.prevFramesPainted;
      this.sharePrevBytes = this.shareBytes;
      this.prevFramesPainted = this.framesPainted;
      this.sendC({
        case: "screenShareStats",
        value: {
          bitrateKbps: Math.round((bytes * 8) / (STATS_PERIOD_MS / 1000) / 1000),
          fps: Math.round(frames / (STATS_PERIOD_MS / 1000)),
          framesDropped: 0,
          keyframesRequested: this.keyframesRequested,
          viewers: 0,
          sending: false,
        },
      });
      this.keyframesRequested = 0;
    }
  }

  // -------------------------------------------------------------------------
  // Teardown

  private end(reason: VoiceEndReason, detail?: string): void {
    if (this.closed) return;
    this.teardown();
    this.events.onEnded(reason, detail);
  }

  private teardown(): void {
    this.closed = true;
    if (this.statsTimer !== null) clearInterval(this.statsTimer);
    for (const w of this.waiters.splice(0)) {
      clearTimeout(w.timer);
      w.reject(new Error("the call is over"));
    }
    this.rejectOpen?.(new Error("the call is over"));
    this.rejectOpen = null;
    for (const pending of this.rpcPending.values()) pending.reject(new Error("the call is over"));
    this.rpcPending.clear();
    this.worker?.terminate();
    this.worker = null;
    this.dc = null;
    this.shareDc = null;
    this.painter = null;
    this.watchingSender = null;
    this.pc?.close();
    this.pc = null;
    for (const track of this.mic?.getTracks() ?? []) track.stop();
    this.mic = null;
    this.mixer?.close();
    this.mixer = null;
    const ws = this.ws;
    this.ws = null;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) ws.close();
  }
}
